import React, { useState } from 'react';

import CrewMemberAutocompleteSingleV2 from 'common/CrewMemberAutocompleteSingleV2';

import PostIncidentDrivingInputs from '../inputs/PostIncidentDrivingInputs';
import ReplacementDepartureLocationInput from '../inputs/ReplacementDepartureLocationInput';
import ReplacementEtdEtaInputs from '../inputs/ReplacementEtdEtaInputs';

import styles from './FatigueReplacementStep.module.css';

/**
 * Leaf step — Driver fatigue + Replacement driver.
 *
 * Layout (v2, redesigned):
 *   - "Replacement box" (bordered container):
 *     - Replacement departure location (city autocomplete)
 *     - Replacement driver (crew autocomplete, capped at 3 rows)
 *     - Replacement ETD / Replacement ETA (each split date + time)
 *   - Post-incident driving (outside the box, split date + time)
 *
 * The original driver's absence timestamp is collected on the common step
 * for this reason; the payload mapper reads it from the shared draft.
 *
 * @param {import('common/activeElements/modals/FlowModal/flowTypes').FlowNodeComponentProps} props
 */
const FatigueReplacementStep = ({ data, onDraftChange }) => {
	const [replacementDriver, setReplacementDriver] = useState(
		data?.replacementDriverId
			? {
					id: data.replacementDriverId,
					fullName: data?.replacementDriverFullName ?? '',
				}
			: null
	);
	const [driving, setDriving] = useState({
		fromTimestamp: data?.driving?.fromTimestamp ?? '',
	});
	const [location, setLocation] = useState({
		fromCityId: data?.replacementInboundTraveling?.fromCityId ?? '',
		fromCityName: data?.replacementInboundTraveling?.fromCityName ?? '',
	});
	const [etdEta, setEtdEta] = useState({
		fromTimestamp: data?.replacementInboundTraveling?.fromTimestamp ?? '',
		toTimestamp: data?.replacementInboundTraveling?.toTimestamp ?? '',
	});

	const emitChange = (patch) => {
		const merged = {
			replacementDriverId: replacementDriver?.id ?? null,
			replacementDriver: replacementDriver ?? null,
			driving,
			location,
			etdEta,
			...patch,
		};
		onDraftChange?.({
			replacementDriverId: merged.replacementDriverId,
			replacementDriver: merged.replacementDriver,
			driving: merged.driving,
			replacementInboundTraveling: {
				fromCityId: merged.location.fromCityId,
				fromCityName: merged.location.fromCityName,
				fromTimestamp: merged.etdEta.fromTimestamp,
				toTimestamp: merged.etdEta.toTimestamp,
			},
		});
	};

	const handleDriverSelection = (selection) => {
		setReplacementDriver(selection);
		emitChange({
			replacementDriverId: selection?.id ?? null,
			replacementDriver: selection ?? null,
		});
	};

	const handleDriverReset = () => {
		setReplacementDriver(null);
		emitChange({ replacementDriverId: null, replacementDriver: null });
	};

	const handleLocationChange = (nextLocation) => {
		setLocation(nextLocation);
		emitChange({ location: nextLocation });
	};

	const handleEtdEtaChange = (nextEtdEta) => {
		setEtdEta(nextEtdEta);
		emitChange({ etdEta: nextEtdEta });
	};

	const handleDrivingChange = (nextDriving) => {
		setDriving(nextDriving);
		emitChange({ driving: nextDriving });
	};

	return (
		<div className={styles.stack}>
			<div className={styles.replacementBox} data-testid="replacement-box">
				<ReplacementDepartureLocationInput value={location} onChange={handleLocationChange} />
				<CrewMemberAutocompleteSingleV2
					label="Replacement driver"
					placeholder="Select a driver"
					optionsToDisplay={3}
					subtitlePrefix="ID: "
					defaultValue={replacementDriver}
					onSelection={handleDriverSelection}
					onReset={handleDriverReset}
				/>
				<ReplacementEtdEtaInputs
					value={etdEta}
					incidentTimestamp={data?.incidentTimestamp}
					onChange={handleEtdEtaChange}
				/>
			</div>

			<PostIncidentDrivingInputs
				value={driving}
				incidentTimestamp={data?.incidentTimestamp}
				replacementEta={etdEta.toTimestamp}
				onChange={handleDrivingChange}
			/>
		</div>
	);
};

export default FatigueReplacementStep;
